import type {
  LayoutMode as PkgLayoutMode,
  LayoutOrientation as PkgLayoutOrientation,
} from '@bn-games/layout-core';
import { SEAT_COUNT, SEATS, type Seat } from '../game/types.js';
import type { TileRotation } from '../render/TileRenderer.js';

export { MOBILE_BREAKPOINT_PX, TABLET_BREAKPOINT_PX } from '@bn-games/layout-core';

export type LayoutMode = PkgLayoutMode;
export type LayoutOrientation = PkgLayoutOrientation;

/**
 * Physical position around the table from the LOCAL player's perspective.
 * The local player always sits at `bottom`; the other three seats fill
 * `right` / `top` / `left` in turn order after them (see `seatPosition`).
 * This sweeps COUNTERCLOCKWISE on screen (bottom → right → top → left) —
 * intentionally: real mahjong seats East/South/West/North counterclockwise
 * around the table (turns proceed counterclockwise), and the "Chi only from
 * the player to your left" language throughout the lessons + rules glossary
 * depends on this exact arrangement — the seat at the local player's
 * on-screen left is always the one immediately before them in turn order
 * (their kamicha). Do not "fix" this to run clockwise; it would contradict
 * standard mahjong convention and break that text.
 */
export type SeatPosition = 'bottom' | 'right' | 'top' | 'left';

/**
 * Screen position for the seat sitting `offset` seats after the viewer in
 * turn order — offset 0 is the viewer themself, always at `bottom`. The
 * single source of truth for the counterclockwise sweep above; both
 * `SEAT_POSITIONS` and `seatPosition` derive from it.
 */
const POSITION_BY_TURN_OFFSET: Readonly<Record<Seat, SeatPosition>> = {
  0: 'bottom',
  1: 'right',
  2: 'top',
  3: 'left',
};

/**
 * Indexed by `Seat` (0=East..3=North) — the seat→screen mapping as seen from
 * seat 0. Prefer `seatPosition(seat, localSeat)` anywhere a specific seat is
 * being placed; this stays for code that enumerates the four screen edges
 * rather than the four seats (layout construction, tests).
 */
export const SEAT_POSITIONS: readonly SeatPosition[] = SEATS.map(
  (seat) => POSITION_BY_TURN_OFFSET[seat],
);

/**
 * Screen position of `seat` from `localSeat`'s point of view: `localSeat`
 * always renders at `bottom` and the other three follow the counterclockwise
 * sweep from there — `SEAT_POSITIONS[(seat - localSeat + SEAT_COUNT) % SEAT_COUNT]`.
 * Solo play and the lessons pass `localSeat = 0`, which makes this exactly
 * `SEAT_POSITIONS[seat]` — the identity every renderer assumed implicitly
 * before online play, where the local player may hold any of the four seats.
 */
export function seatPosition(seat: Seat, localSeat: Seat): SeatPosition {
  return POSITION_BY_TURN_OFFSET[turnOffset(seat, localSeat)];
}

/**
 * `(seat - localSeat + SEAT_COUNT) % SEAT_COUNT`, spelled as a switch so the
 * result is typed `Seat`: the arithmetic form widens to `number`, which can
 * neither index a `Record<Seat, …>` nor be narrowed back without a cast.
 */
function turnOffset(seat: Seat, localSeat: Seat): Seat {
  const delta = seat - localSeat;
  switch (delta < 0 ? delta + SEAT_COUNT : delta) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    default:
      return 0;
  }
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * A seat's hand band along one edge of the table. `rotation` is the quarter
 * turn applied to that seat's tiles so they face table centre (bottom upright,
 * top upside-down, sides edge-on).
 */
export interface SeatZone extends Rect {
  readonly position: SeatPosition;
  readonly rotation: TileRotation;
}

/**
 * Resolved layout snapshot in canvas/viewport pixels. Renderers read `table`
 * for the felt table (fills the full viewport, like domino's canvas),
 * `seatZones` for the four racks, `pond` + `pondSeats` for the central
 * discard area, and `coachSafeArea` as the dock position for the FLOATING
 * `CoachPanel` overlay (desktop only; zero-sized elsewhere) — it is not
 * subtracted from `table`, so the panel may sit on top of the table's right
 * seat band while shown, the same way ActionBar/LessonCard float over it.
 */
export interface LayoutConfig {
  readonly mode: LayoutMode;
  readonly orientation: LayoutOrientation;
  readonly viewport: { readonly width: number; readonly height: number };
  readonly devicePixelRatio: number;
  readonly table: Rect;
  readonly seatZones: Readonly<Record<SeatPosition, SeatZone>>;
  readonly pond: Rect;
  readonly pondSeats: Readonly<Record<SeatPosition, Rect>>;
  readonly coachSafeArea: Rect;
}

export type LayoutChangeCallback = (config: LayoutConfig) => void;
